<template>
    <div  class="c-video" v-bind:class="{ 'is-video-active': videoActive, 'is-ui-show': uiShow }">
        <core-object-fit class="u-absolute u-pos-tl u-fit u-overflow-h" v-bind:width="1152" v-bind:height="720" v-bind:type="'contain'">
            <video ref="video" v-on:click="toggleVideo" v-bind:poster="poster" v-bind:autoplay="autoplay" class="js-object-fit u-absolute u-pos-tl u-fit">
                <source v-bind:src="src" type='video/mp4' />
            </video>
        </core-object-fit>

        <svg class="c-video-spinner u-absolute u-pos-center u-pointer-none" v-bind:class="{ 'is-video-ready': videoCanplay }"  width="65px" height="65px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="c-video-spinner-path" fill="none" stroke-width="4" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
        </svg>

        <cursor-follow v-if="!isTouch" v-bind:on-click="toggleVideo" v-bind:auto-hide="videoActive" v-bind:sticky="true" class="u-absolute u-pos-tl u-fit">
            <template slot="cursor">
                <div class="c-video-cursor">
                    <div class="c-video-play u-absolute u-pos-tl u-fit u-flex u-align-items-c u-justify-content-c">
                        <div class="c-video-play-icon"></div>
                    </div>
                    <div class="c-video-pause u-absolute u-pos-tl u-fit u-flex u-align-items-c u-justify-content-c">
                        <div class="c-video-pause-icon"></div>
                    </div>
                </div>
            </template>
        </cursor-follow>

        <video-timeline v-bind:pourcent="videoPourcent" v-bind:on-seek="onSeek" class="c-video-timeline u-absolute u-pos-bl u-fit-w u-overflow-h"></video-timeline>

        <template v-if="isTouch">
            <button v-on:click="toggleVideo" class="c-video-play-pause t-btn u-absolute u-pos-center">
                <div class="c-video-play u-absolute u-pos-center u-flex u-align-items-c u-justify-content-c">
                    <div class="c-video-play-icon"></div>
                </div>
                <div class="c-video-pause u-absolute u-pos-center u-flex u-align-items-c u-justify-content-c">
                    <div class="c-video-pause-icon"></div>
                </div>
            </button>
        </template>

        <div class="c-video-timer u-absolute u-pos-bl t-text--xs u-uppercase u-font-heading u-color--white u-flex u-align-items-c">
            <div v-if="timer">{{ currentTimeLabel }} / {{ durationLabel }}</div>
        </div>

        <button v-if="sound" v-on:click="toggleSound" v-bind:class="{ 'is-active': !videoMuted }" class="c-video-sound t-btn u-absolute u-pos-br u-flex u-justify-content-sb">
            <span class="c-video-sound-line c-video-sound-line--0 u-pointer-none"></span>
            <span class="c-video-sound-line c-video-sound-line--1 u-pointer-none"></span>
            <span class="c-video-sound-line c-video-sound-line--2 u-pointer-none"></span>
        </button>

        <slot></slot>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import { pointermove } from '@/utilities/pointer';

    import videoTimeline from '@/components/videoTimeline';
    import coreObjectFit from '@/components/coreObjectFit';
    import cursorFollow from '@/components/cursorFollow';

    export default {
        name: 'VideoPlayer',

        components: {
            videoTimeline,
            coreObjectFit,
            cursorFollow,
        },

        props: {
            src: { type: String, default: '' },
            poster: { type: String, default: '' },
            autoplay: { type: Boolean, default: false },
            onClick: { type: Function, default: () => this },
            onEnded: { type: Function, default: () => this },
            timeUpdate: { type: Function, default: () => this },
            sound: { type: Boolean, default: true },
            timer: { type: Boolean, default: false },
        },

        data() {
            return {
                videoActive: false,
                videoPourcent: 0,
                videoMuted: false,
                duration: 0,
                currentTime: 0,
                uiShow: false,
                videoCanplay: false,
            };
        },

        computed: {
            ...mapState('window', [
                'width',
                'isTouch',
                'isAndroid',
            ]),

            ...mapGetters('window', [
                'isMobile',
            ]),

            durationLabel() {
                var minutes = "0" + Math.floor(this.duration / 60);
                var seconds = "0" + (Math.floor(this.duration) - minutes * 60);

                return minutes.substr(-2) + ":" + seconds.substr(-2);
            },

            currentTimeLabel() {
                var minutes = "0" + Math.floor(this.currentTime / 60);
                var seconds = "0" + (Math.floor(this.currentTime) - minutes * 60);

                return minutes.substr(-2) + ":" + seconds.substr(-2);
            },

            autoHide() {
                if (this.isTouch) {
                    return false;
                }

                return true;
            }
        },

        watch: {
            videoActive: 'onVideoActiveChange',
            src: 'onSrcChange',
        },

        created() {
            this._onPointerMove = this.onPointerMove.bind(this);

            this._onVideoPlay = this.onVideoPlay.bind(this);
            this._onVideoPause = this.onVideoPause.bind(this);
            this._onVideoMetaData = this.onVideoMetaData.bind(this);
            this._onTimeUpdate = this.onTimeUpdate.bind(this);
            this._onVideoEnded = this.onVideoEnded.bind(this);
            this._onVideoCanplay = this.onVideoCanplay.bind(this);
            this._onVideoWaiting = this.onVideoWaiting.bind(this);

            this.videoActive = this.autoplay;
        },

        mounted() {
            if (this.isTouch){
                this.uiShow = true;
            }

            this.$el.addEventListener(pointermove, this._onPointerMove, false);

            this.$refs.video.addEventListener('playing', this._onVideoPlay, false);
            this.$refs.video.addEventListener('pause', this._onVideoPause, false);
            this.$refs.video.addEventListener('loadedmetadata', this._onVideoMetaData, false);
            this.$refs.video.addEventListener('timeupdate', this._onTimeUpdate, false);
            this.$refs.video.addEventListener('ended', this._onVideoEnded, false);
            this.$refs.video.addEventListener('canplay', this._onVideoCanplay, false);
            this.$refs.video.addEventListener('waiting', this._onVideoWaiting, false);
        },

        beforeDestroy() {
            this.$el.removeEventListener(pointermove, this._onPointerMove);

            this.$refs.video.removeEventListener('playing', this._onVideoPlay);
            this.$refs.video.removeEventListener('paused', this._onVideoPause);
            this.$refs.video.removeEventListener('loadedmetadata', this._onVideoMetaData);
            this.$refs.video.removeEventListener('timeupdate', this._onTimeUpdate);
            this.$refs.video.removeEventListener('ended', this._onVideoEnded);
            this.$refs.video.removeEventListener('canplay', this._onVideoCanplay);
            this.$refs.video.removeEventListener('waiting', this._onVideoWaiting);
        },

        methods: {
            onSrcChange() {
                this.videoCanplay = false;
                this.videoActive = false;
                this.$refs.video.load();

                if (this.autoplay) {
                    this.onPlay();
                }
            },

            onVideoCanplay() {
                this.videoCanplay = true;
            },

            onVideoWaiting() {
                this.videoCanplay = false;
            },

            onPlay() {
                this.$refs.video.play();
            },

            onPause() {
                this.$refs.video.pause();
            },

            onVideoPlay() {
                this.videoActive = true;
            },

            onVideoPause() {
                this.videoActive = false;
            },

            onVideoMetaData() {
                this.duration = this.$refs.video.duration;
            },

            onTimeUpdate() {
                this.currentTime = this.$refs.video.currentTime;
                this.videoPourcent = this.currentTime / this.duration;

                this.timeUpdate(this.currentTime);
            },

            onSeek(ratio) {
                this.$refs.video.currentTime = ratio * this.duration;
            },

            toggleVideo() {
                const method = (this.videoActive) ? 'pause' : 'play';
                this.$refs.video[method]();

               if (this.isAndroid) {
                    if (!this.videoActive) {
                        this.launchIntoFullscreen(this.$refs.video);
                    } else {
                        this.exitFullscreen()
                    }
                }
            },

            onVideoEnded() {
                this.videoActive = false;
                this.onEnded();
            },

            onVideoActiveChange() {
                this.onClick(this.videoActive);
            },

            onPointerMove() {
                // show timeline only when cursor move
                this.uiShow = true;

                clearTimeout(this.timerTimeline);
                this.timerTimeline = setTimeout( () => {
                    this.uiShow = false;
                }, 2000);
            },

            toggleSound() {
                if (this.videoMuted) {
                    this.$refs.video.volume = 1;
                } else {
                    this.$refs.video.volume = 0;
                }

                this.videoMuted = !this.videoMuted;
            },

            launchIntoFullscreen(element) {
                if(element.requestFullscreen) {
                    element.requestFullscreen();
                } else if(element.mozRequestFullScreen) {
                    element.mozRequestFullScreen();
                } else if(element.webkitRequestFullscreen) {
                    element.webkitRequestFullscreen();
                } else if(element.msRequestFullscreen) {
                    element.msRequestFullscreen();
                }
            },

            exitFullscreen() {
                if(document.exitFullscreen) {
                    document.exitFullscreen();
                } else if(document.mozCancelFullScreen) {
                    document.mozCancelFullScreen();
                } else if(document.webkitExitFullscreen) {
                    document.webkitExitFullscreen();
                }
            },
        },
    };
</script>

<style lang="stylus" scoped>
    @import '../styles/settings/*'

    .c-video {
        cursor pointer
    }

    .c-video-play-pause {
        width 12px
        height 12px
    }

    .c-video-timer {
        left 45px
        bottom 35px

        opacity 1
        transition opacity 0.6s $ease-out-quart

        @media $breakpoints.sm {
            left 15px
            bottom 15px
        }

        .is-video-active & {
            opacity 0
        }

        .is-ui-show & {
            opacity 1
        }
    }

    .c-video-timeline {
        opacity 1
        transition opacity 0.6s $ease-out-quart

        .is-video-active & {
            opacity 0
        }

        .is-ui-show & {
            opacity 1
        }
    }

    .c-video-pause {
        opacity 0
        transition opacity 0.6s $ease-out-quart

        .is-video-active & {
            opacity 1
        }

        &-icon {
            width 8px
            height 12px
            position relative

            &:before {
                content ""
                position absolute
                top 0
                left 0
                width 2px
                height 100%

                background $colors['white'];
            }

            &:after {
                content ""
                position absolute
                top 0
                right 0
                width 2px
                height 100%

                background $colors['white'];
            }
        }
    }

    .c-video-play {
        opacity 1
        transition opacity 0.6s $ease-out-quart

        .is-video-active & {
            opacity 0
        }

        &-icon {
            margin-left 3px
            position relative
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 7px 0 7px 10px;
            border-color: transparent transparent transparent $colors['white'];
        }
    }

    .c-video-cursor {
        opacity 1
        transition opacity 0.6s $ease-out-quart

        width 50px
        height 50px
        // border-radius 50%
        // border 2px solid white

        .is-video-active & {
            opacity 0
        }

        .is-ui-show & {
            opacity 1
        }
    }

    .c-video-sound {
        width 14px
        height 22px

        right 45px
        bottom 35px

        opacity 1
        transition opacity 0.6s $ease-out-quart

        @media $breakpoints.sm {
            right 25px
        }

        .is-video-active & {
            opacity 0
        }

        .is-ui-show & {
            opacity 1
        }

        &-line {
            display block
            width 2px
            height 22px

            background $colors['white'];
            transform-origin bottom
            transform translateZ(0)

            &--0 {
                animation scaleLine 0.8s linear infinite 0.1s paused
            }

            &--1 {
                animation scaleLine 0.8s linear infinite 0.3s paused
            }

            &--2 {
                animation scaleLine 0.8s linear infinite 0.4s paused
            }

            &--0, &--1, &--2 {
                transform scaleY(0.2) translateZ(0)

            }
        }

        &.is-active {
            .c-video-sound-line--0 {
                transform scaleY(0.4)
            }

            .c-video-sound-line--1 {
                transform scaleY(1)
            }

            .c-video-sound-line--2 {
                transform scaleY(0.7)
            }

            .c-video-sound-line--0, .c-video-sound-line--1, .c-video-sound-line--2 {
                .is-video-active & {
                    animation-play-state running
                }
            }


        }
    }

    @keyframes scaleLine {
        0%, 100% {
            transform: scaleY(0.2) translateZ(0)
        }
        50% {
            transform: scaleY(1) translateZ(0)
        }
    }

    .c-video-spinner {
        animation: rotationSpinner 1.4s linear infinite;

        opacity 0
        visibility hidden
        transition opacity 0.6s $ease-out-quart, visibility 0.6s $ease-out-quart

        .is-video-active & {
            opacity 1
            visibility visible
        }

        &.is-video-ready {
            animation none
            display none

            .c-video-spinner-path {
                animation none
            }
        }

        &-path {
            stroke $colors['white'];

            stroke-dasharray 187
            stroke-dashoffset 0
            transform-origin center

            animation dashSpinner 1.4s ease-in-out infinite
            transform translateZ(0)

            .is-video-ready & {
                animation none
            }
        }
    }

    @keyframes rotationSpinner {
        0% {
            transform translate(-50%, -50%) rotate(0deg) translateZ(0)
        }
        100% {
            transform translate(-50%, -50%) rotate(270deg) translateZ(0)
        }
    }

    @keyframes dashSpinner {
        0% {
            stroke-dashoffset: 187
        }
        50% {
            stroke-dashoffset: 46.75
            transform: rotate(135deg) translateZ(0)
        }
        100% {
            stroke-dashoffset: 187
            transform: rotate(450deg) translateZ(0)
        }
    }

</style>
